/**
 * Panel state for Ask Vera.
 *
 * One store rather than two (a "UI prefs" store and a "conversation" store), because the
 * two are not independent: opening the panel with a question and starting a conversation
 * are a single user action, and splitting them would mean two stores that must be kept in
 * step by every caller.
 *
 * What persists and what does not is the interesting part — see `partialize` below.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { AskConversation, AskMessage } from './types'

/** Which half of the panel is showing: the live conversation, or the list of past ones. */
export type AskView = 'conversation' | 'history'

/**
 * Continuous drag range for the panel (VRG-227 polish pass).
 *
 * This replaced a three-stop SNAP (400 / 560 / 760). The snap's own reasoning — an
 * arbitrary width leaves the page at an arbitrary width, and tables reflow into
 * layouts nobody designed — is true of any width, snapped or not, so it was never an
 * argument specific to snapping; it just meant continuous resize needed a floor and a
 * ceiling instead of three fixed points. `ASK_WIDTH_MIN` is the floor (the composer's
 * send button + a reasonable line length still fit); `ASK_WIDTH_MAX_RATIO` is a
 * proportion of the viewport, not a fixed px ceiling, so the panel cannot eat the page
 * on a small laptop the way a fixed 900px max would.
 */
export const ASK_WIDTH_MIN = 360
export const ASK_WIDTH_MAX_RATIO = 0.65

/**
 * Wider than the 480 it opened at. A panel that answers with a paragraph plus Sources and
 * Terms was landing in a column narrow enough that most answers wrapped four or five times
 * before the first real clause finished. Still comfortably inside the 65% ceiling on a
 * 1280 viewport (832px), so opening it never buries the page it is meant to sit beside.
 */
export const DEFAULT_ASK_WIDTH = 640

/**
 * The open/close slide's timing (VRG-227), shared between the panel itself (AskPanel,
 * which transitions `transform`) and `main` (Layout.tsx, which transitions `width` so it
 * settles alongside the panel rather than snapping). Living here rather than being
 * declared twice means the two can never drift apart. The curve matches the one the rest
 * of the app already uses for page-slide transitions.
 */
export const ASK_PANEL_SLIDE_DURATION_MS = 240
export const ASK_PANEL_SLIDE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'

/**
 * Clamp a candidate width to [`ASK_WIDTH_MIN`, `ASK_WIDTH_MAX_RATIO` of the viewport].
 *
 * The FLOOR IS APPLIED LAST, and a degenerate viewport yields no ceiling at all. Both
 * details are load-bearing, and the previous version had neither:
 *
 *     Math.min(max, Math.max(ASK_WIDTH_MIN, px))   // floor nested inside the ceiling
 *
 * With `window.innerWidth === 0` — which happens in an offscreen, backgrounded or
 * not-yet-laid-out frame, and does happen in the Claude preview pane — `max` is 0, so
 * `Math.min(0, 360)` is 0 and the 360px minimum is defeated by its own ceiling. That 0
 * is then persisted, and because `onRehydrateStorage` re-clamps through this same
 * function, every subsequent load REPRODUCES it instead of repairing it: the panel
 * comes back as a ~50px sliver with one character per line, permanently. Observed
 * live on 2026-08-27 with `storedWidth: 0` against a 1280px viewport.
 *
 * Ordering the operations so the floor wins makes the invariant unconditional — the
 * result is never below `ASK_WIDTH_MIN` no matter what the ceiling computes to — and
 * treating a falsy `innerWidth` as "no information about the viewport" means a
 * measurement taken at the wrong moment can no longer shrink the panel. Together they
 * also make the bad persisted value self-healing on the next load.
 */
export const clampAskWidth = (px: number): number => {
  const viewport = typeof window === 'undefined' ? 0 : window.innerWidth
  const max = viewport > 0 ? viewport * ASK_WIDTH_MAX_RATIO : Number.POSITIVE_INFINITY
  return Math.max(ASK_WIDTH_MIN, Math.min(max, px))
}

/** Past conversations kept per user. Old ones fall off rather than growing without bound. */
const MAX_HISTORY = 30

const newId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

const titleFor = (question: string) => {
  const t = question.trim().replace(/\s+/g, ' ')
  return t.length > 60 ? `${t.slice(0, 57)}…` : t || 'Untitled'
}

interface AskState {
  open: boolean
  width: number
  view: AskView

  /**
   * Fullscreen takeover (VRG-227). Deliberately NOT persisted — a full-viewport panel
   * left on from a previous session would be a surprising thing to land on after a
   * reload, unlike `width`/`open`, which are quiet enough to carry over. Collapsing
   * just stops READING this flag; `width` itself is never touched by expanding, so
   * un-expanding returns to exactly the size it was dragged to before.
   */
  expanded: boolean

  conversations: AskConversation[]
  activeId: string | null

  /**
   * The user these persisted preferences belong to.
   *
   * zustand's `persist` writes one localStorage key for the origin, with no notion of who
   * is signed in — so without this, signing out and back in as someone else would inherit
   * the previous user's panel width and, worse, their conversation history. `claimFor`
   * below is what makes the persistence per-user.
   */
  ownerId: string | null

  /** A question handed over from the ⌘K palette, waiting for the panel to pick it up. */
  pendingQuestion: string | null

  /**
   * A question handed over from the suggested-questions carousel, waiting for the
   * composer to pick it up.
   *
   * Deliberately a SEPARATE field from `pendingQuestion`, not a shared channel — the two
   * mean different things and must stay distinguishable by whoever consumes them:
   * `pendingQuestion` is sent immediately (the ⌘K palette already committed the user to
   * asking it), `draftQuestion` only fills the composer for the user to send or edit. A
   * suggestion is a starting point, not a submission — several of the corpus's questions
   * are close-but-not-exact ("which product gets scanned the most" when someone meant a
   * specific product), so sending on click would spend a query on a question they never
   * quite asked (VRG-227).
   */
  draftQuestion: string | null

  /**
   * True for the duration of a live drag on the resize handle (VRG-227 slide fix).
   *
   * `Layout`'s `main` column gets its own `width` transition so it settles smoothly
   * when the panel opens or closes, rather than snapping instantly (see AskPanel's
   * docblock). That transition must be OFF while dragging — the handle already writes
   * `main`'s available space on every pointermove via a direct DOM read of the
   * pointer's position, and a 240ms width transition fighting that would make the
   * panel visibly lag behind the pointer instead of tracking it 1:1. Deliberately not
   * persisted: a drag never survives a reload anyway.
   */
  resizing: boolean

  openPanel: () => void
  closePanel: () => void
  togglePanel: () => void
  setWidth: (px: number) => void
  setResizing: (resizing: boolean) => void
  setView: (view: AskView) => void
  toggleExpanded: () => void
  setExpanded: (expanded: boolean) => void

  /** Open the panel and queue `question` as the next thing to ask. */
  ask: (question: string) => void
  consumePendingQuestion: () => string | null

  /**
   * Open the panel and place `question` in the composer, unsent. The user presses
   * Enter (or edits first) — distinct from `ask`, which sends immediately.
   */
  prefill: (question: string) => void
  consumeDraftQuestion: () => string | null

  startConversation: () => string
  openConversation: (id: string) => void
  appendMessage: (conversationId: string, message: AskMessage) => void
  updateMessage: (conversationId: string, messageId: string, patch: Partial<AskMessage>) => void
  deleteConversation: (id: string) => void
  /**
   * Bulk delete, for the history view's multi-select (VRG-227). A single `set` call
   * rather than looping `deleteConversation` — looping would fire N re-renders and,
   * worse, recompute `activeId` against an already-mutated array on each iteration
   * instead of the pre-delete snapshot every id in the batch was chosen against.
   */
  deleteConversations: (ids: string[]) => void
  clearHistory: () => void

  /** Adopt the store for `userId`, discarding another user's persisted state. */
  claimFor: (userId: string | null) => void
}

const touch = (c: AskConversation): AskConversation => ({ ...c, updatedAt: Date.now() })

export const useAskStore = create<AskState>()(
  persist(
    (set, get) => ({
      open: false,
      width: DEFAULT_ASK_WIDTH,
      view: 'conversation',
      expanded: false,
      conversations: [],
      activeId: null,
      ownerId: null,
      pendingQuestion: null,
      draftQuestion: null,
      resizing: false,

      openPanel: () => set({ open: true }),
      // Collapsing back out of fullscreen on close, too — reopening later should not
      // land back in the fullscreen takeover from a session that never explicitly
      // collapsed it.
      closePanel: () => set({ open: false, expanded: false }),
      togglePanel: () => set(s => ({ open: !s.open })),
      setWidth: px => set({ width: clampAskWidth(px) }),
      setResizing: resizing => set({ resizing }),
      setView: view => set({ view }),
      toggleExpanded: () => set(s => ({ expanded: !s.expanded })),
      setExpanded: expanded => set({ expanded }),

      ask: question =>
        set({
          open: true,
          view: 'conversation',
          pendingQuestion: question,
        }),

      consumePendingQuestion: () => {
        const q = get().pendingQuestion
        if (q !== null) set({ pendingQuestion: null })
        return q
      },

      prefill: question =>
        set({
          open: true,
          view: 'conversation',
          draftQuestion: question,
        }),

      consumeDraftQuestion: () => {
        const q = get().draftQuestion
        if (q !== null) set({ draftQuestion: null })
        return q
      },

      startConversation: () => {
        const id = newId()
        const now = Date.now()
        set(s => ({
          activeId: id,
          view: 'conversation',
          conversations: [
            { id, title: 'New question', createdAt: now, updatedAt: now, messages: [] },
            ...s.conversations,
          ].slice(0, MAX_HISTORY),
        }))
        return id
      },

      openConversation: id =>
        set({
          activeId: id,
          view: 'conversation',
        }),

      appendMessage: (conversationId, message) =>
        set(s => ({
          conversations: s.conversations.map(c =>
            c.id !== conversationId
              ? c
              : touch({
                  ...c,
                  // The first user message names the conversation in history. Later ones
                  // do not rename it — a thread keeps the title of the thing it started as.
                  title:
                    c.messages.length === 0 && message.role === 'user'
                      ? titleFor(message.text)
                      : c.title,
                  messages: [...c.messages, message],
                }),
          ),
        })),

      updateMessage: (conversationId, messageId, patch) =>
        set(s => ({
          conversations: s.conversations.map(c =>
            c.id !== conversationId
              ? c
              : touch({
                  ...c,
                  messages: c.messages.map(m => (m.id === messageId ? { ...m, ...patch } : m)),
                }),
          ),
        })),

      deleteConversation: id =>
        set(s => ({
          conversations: s.conversations.filter(c => c.id !== id),
          activeId: s.activeId === id ? null : s.activeId,
        })),

      deleteConversations: ids => {
        if (ids.length === 0) return
        const idSet = new Set(ids)
        set(s => ({
          conversations: s.conversations.filter(c => !idSet.has(c.id)),
          activeId: s.activeId !== null && idSet.has(s.activeId) ? null : s.activeId,
        }))
      },

      clearHistory: () => set({ conversations: [], activeId: null }),

      claimFor: userId =>
        set(s => {
          if (s.ownerId === userId) return s
          // Either a first claim on a fresh store, or a different user signed in. In both
          // cases the safe move is to keep nothing: conversation history can quote company
          // data, so inheriting it across accounts on a shared machine is a leak, not a
          // convenience.
          return {
            ownerId: userId,
            conversations: [],
            activeId: null,
            pendingQuestion: null,
            draftQuestion: null,
            open: false,
            width: DEFAULT_ASK_WIDTH,
            view: 'conversation',
            expanded: false,
          }
        }),
    }),
    {
      name: 'vera:ask',
      /**
       * In-flight state is deliberately NOT persisted. A message left `streaming` when the
       * tab closed would rehydrate as a turn that never finishes and cannot be cancelled,
       * so `partialize` drops the active pointer and the queued question, and the rehydrate
       * pass below settles any half-written message.
       */
      partialize: state => ({
        width: state.width,
        open: state.open,
        ownerId: state.ownerId,
        conversations: state.conversations,
      }),
      onRehydrateStorage: () => state => {
        if (!state) return

        /**
         * Re-clamp the persisted width against THIS viewport.
         *
         * `clampAskWidth` ran when the width was set, against the window the user was
         * dragging in. It never ran again — so a width dragged on a large monitor came
         * back unclamped on a laptop and the panel swallowed the screen. Observed at
         * 1197px on a 1280px viewport, where the ceiling is 65% (832px): the page was
         * a sliver and looked like the panel had opened fullscreen on its own.
         *
         * The ceiling is a function of the viewport, so it has to be re-applied wherever
         * the viewport might have changed since — which is every load.
         */
        state.width = clampAskWidth(state.width)
        // A conversation persisted before VRG-227 still carries a `mode: 'help' |
        // 'analytics'` field in localStorage. It is no longer part of `AskConversation`,
        // so it is simply along for the ride here as an untyped, unread property —
        // nothing rehydrates it into a live field, and nothing throws on its presence.
        state.conversations = state.conversations.map(c => ({
          ...c,
          messages: c.messages.map(m =>
            m.status === 'streaming'
              ? { ...m, status: 'cancelled' as const, error: undefined }
              : m,
          ),
        }))
      },
    },
  ),
)
